import React, { useEffect, useState } from "react";
import { IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useDispatch, useSelector } from "react-redux";
import { addPolicy } from "../policies/policySlice";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { storage } from "../../firebase/firebaseConfig";
import { toast } from "react-toastify";
import SuccessDialog from "../addPolicy/SuccessDialog";
import { doc, setDoc, updateDoc, getDoc, collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/firebaseConfig";
import { v4 as uuidv4 } from "uuid";
import TextField from "@mui/material/TextField";
import { Button, Box, FormControl, InputLabel, Select, MenuItem, Autocomplete, CircularProgress } from "@mui/material";
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { firebaseCache } from "../../utils/firebaseCache";
import { getCompaniesByCategory, getVehicleMakesByType, getPolicyTypesByCategory } from "../../data/dropdowns";

const RenewPolicy = ({ isOpen, onClose, prefillData, pdfFile, mode = "edit", existingPolicyId, existingCustomerId, initialCustomerData, onSuccess, agentId }) => {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Use passed agentId or fallback to current user's UID
  const effectiveAgentId = agentId || user?.uid;

  // Reusable TextField styling
  const textFieldStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      height: "56px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: "14px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "2px",
      },
      "&.Mui-error fieldset": {
        borderColor: "#d32f2f",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "16px 12px",
      height: "24px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "#757575",
      "&.Mui-focused": {
        fontSize: "14px",
        color: "#0063F7",
      },
      "&.Mui-error": {
        color: "#d32f2f",
      },
    },
    "& .MuiFormHelperText-root": {
      margin: "4px 0 0 0",
      fontSize: "12px",
      "&.Mui-error": {
        color: "#d32f2f",
      },
    }
  };

  // Reusable Autocomplete styling
  const autocompleteStyles = {
    width: "100%",
    "& .MuiInputBase-root": {
      height: "56px",
      borderRadius: "8px",
      borderWidth: "1px",
      fontSize: "14px",
      backgroundColor: "#FFFFFF",
      "& fieldset": {
        borderColor: "#E0E0E0",
        borderWidth: "1px",
      },
      "&:hover fieldset": {
        borderColor: "#BDBDBD",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#0063F7",
        borderWidth: "2px",
      },
    },
    "& .MuiInputBase-input": {
      padding: "16px 12px",
      height: "24px",
      display: "flex",
      alignItems: "center",
    },
    "& .MuiInputLabel-root": {
      fontSize: "14px",
      color: "#757575",
      "&.Mui-focused": {
        fontSize: "14px",
        color: "#0063F7",
      },
    }
  };

  const [category, setCategory] = useState("motor");
  const [subCategory, setSubCategory] = useState("medical");

  const [file, setFile] = useState(pdfFile || null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [finalPdfUrl, setFinalPdfUrl] = useState("");
  const [existingPdfUrl, setExistingPdfUrl] = useState(""); // Store existing PDF URL for edit mode
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showValidationBanner, setShowValidationBanner] = useState(false);

  // Local state for the PDF file to be uploaded
  const [pdfFileState, setPdfFileState] = useState(pdfFile || null);

  const [formData, setFormData] = useState({
    name: initialCustomerData?.name || "",
    mobile: initialCustomerData?.mobile || "",
    email: initialCustomerData?.email || "",
    address: initialCustomerData?.address || "",
    vehicleType: "",
    vehicleNumber: "",
    vehicleMake: "",
    vehicleModel: "",
    policyCompany: "",
    policyType: "",
    policyNumber: "",
    sumInsured: "",
    expiryDate: "",
    premiumAmount: "",
    paidAmount: "",
    modeOfPayment: "",
    remarks: "",
    referredBy: "",
    netPremium: "",
    discount: "",
    tpPremium: "",
    odPremium: "",
    amountPayable: ""
  });

  // Dropdown data states - MUST be defined AFTER formData
  // Dropdown data states - MUST be defined AFTER formData
  // Using useState for these to allow effects to update them
  const [vehicleTypes] = useState(["Two Wheeler", "Private Vehicle", "Commercial Vehicle"]);
  const [availableMakes, setAvailableMakes] = useState([]);
  const [availableCompanies, setAvailableCompanies] = useState([]);
  const [availablePolicyTypes, setAvailablePolicyTypes] = useState([]);

  // Keep these as helpers if needed, or remove if unused. 
  // Based on usage, 'companies' and 'policyTypes' might be used for initial load or referencing accessors.
  // But given the errors, let's strictly define what is missing.
  // Ideally, we shouldn't mix derived consts and state if they conflict.

  const [paymentModes] = useState(["Cash", "UPI", "UPI+Cash", "Cheque", "Online Transfer", "Online Transfer+Cash"]);
  const [errors, setErrors] = useState({});
  const [commissionData, setCommissionData] = useState({ commissionRate: 0, tdsRate: 2 });



  useEffect(() => {
    // Toggle body class to signal that a modal is open, used to disable sidebar
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup on unmount to be safe
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  useEffect(() => {
    console.log("🔄 Customer data fetch useEffect triggered:", { existingCustomerId, prefillData, mode });
    // If an existing customer is provided (renewal or add-from-client), fetch from DB and prefill
    const maybePrefillFromDb = async () => {
      try {
        // Fetch customer data for renewals (update) and new policies, but not strict edits
        if (existingCustomerId && effectiveAgentId && mode !== "edit") {
          const customerRef = doc(db, `agents/${effectiveAgentId}/customers/${existingCustomerId}`);
          const snap = await getDoc(customerRef);
          if (snap.exists()) {
            const c = snap.data();
            console.log("✅ Prefilling customer data from existing customer:", c);
            setFormData((prev) => ({
              ...prev,
              name: c.name || "",
              mobile: c.mobile || "",
              email: c.email || "",
              address: c.address || "",
              referredBy: c.referredBy || "",
            }));
          }
        } else if (prefillData) {
          // Fall back to prefill from AI if no existing customer
          setFormData((prev) => ({
            ...prev,
            name: prefillData.customer?.name || prev.name,
            mobile: prefillData.customer?.phone || prev.mobile,
            email: prefillData.customer?.email || prev.email,
            address: prefillData.customer?.address || prev.address,
            referredBy: prev.referredBy,
          }));
        }
      } catch (_) {
        // ignore prefill errors
      }
    };
    maybePrefillFromDb();
  }, [existingCustomerId, effectiveAgentId, prefillData, mode]);

  useEffect(() => {
    if (prefillData) {
      console.log("🔄 PDF data received:", prefillData);

      // Initialize file for upload
      if (prefillData.rawFile) {
        setPdfFileState(prefillData.rawFile);
      }

      let initialCategory = "motor";
      if (prefillData.policyCategory === "medical" || prefillData.policyCategory === "life") {
        initialCategory = prefillData.policyCategory;
      } else if (prefillData.policyCategory === "non-motor") {
        initialCategory = "health";
      }
      setCategory(initialCategory);

      if (prefillData.policyCategory === "non-motor") {
        setSubCategory(prefillData.policyCategoryType || "medical");
      }

      console.log("🔄 Processing PDF extracted data");

      // Auto-fill vehicle type and make with closest matches
      const extractedVehicleType = prefillData.vehicle?.type || "";
      const extractedVehicleMake = prefillData.vehicle?.make || "";

      const matchedVehicleType = findClosestMatch(extractedVehicleType, vehicleTypes);

      setFormData((prev) => {
        // For renewals/client uploads with existingCustomerId, preserve customer data from DB
        // Only use PDF data for customer fields if there's no existing customer data
        const useExistingCustomerData = existingCustomerId && (prev.name || prev.mobile || prev.email || prev.address);

        return {
          ...prev,
          // Preserve existing customer data if available (from DB), otherwise use PDF data
          name: useExistingCustomerData ? prev.name : (prefillData.customer?.name || prev.name || ""),
          mobile: useExistingCustomerData ? prev.mobile : (prefillData.customer?.phone || prev.mobile || ""),
          email: useExistingCustomerData ? prev.email : (prefillData.customer?.email || prev.email || ""),
          address: useExistingCustomerData ? prev.address : (prefillData.customer?.address || prev.address || ""),
          referredBy: useExistingCustomerData ? prev.referredBy : (prev.referredBy || ""),
          // Policy and vehicle fields always come from PDF
          vehicleType: matchedVehicleType,
          vehicleNumber: prefillData.vehicle?.number || "",
          vehicleMake: "", // Will be updated in next useEffect
          vehicleModel: prefillData.vehicle?.model || "",
          policyCompany: "", // Will be set by the auto-fill useEffect
          policyType: "", // Will be set by the auto-fill useEffect
          policyNumber: prefillData.policy?.policyNumber || "",
          sumInsured: prefillData.policy?.sumInsured || "",
          expiryDate: prefillData.policy?.renewalDate || "",
          premiumAmount: prefillData.payment?.totalAmount || "",
          paidAmount: prefillData.payment?.receivedAmount || "",
          tpPremium: prefillData.payment?.tpPremium || "",
          odPremium: prefillData.payment?.odPremium || "",
          discount: "0",
          amountPayable: prefillData.payment?.totalAmount || ""
        };
      });
    }
  }, [prefillData, vehicleTypes]);

  // Auto-fill vehicle make when type changes and makes are available
  useEffect(() => {
    if (prefillData && formData.vehicleType && availableMakes.length > 0) {
      const extractedVehicleMake = prefillData.vehicle?.make || "";
      if (extractedVehicleMake) {
        const matchedVehicleMake = findClosestMatch(extractedVehicleMake, availableMakes);
        if (matchedVehicleMake && matchedVehicleMake !== formData.vehicleMake) {
          setFormData(prev => ({ ...prev, vehicleMake: matchedVehicleMake }));
        }
      }
    }
  }, [formData.vehicleType, availableMakes, prefillData]);

  // Auto-fill policy company when category changes and companies are available
  useEffect(() => {
    if (prefillData && category && availableCompanies.length > 0) {
      const extractedPolicyCompany = prefillData.policy?.company || "";
      console.log("🔍 Auto-fill Policy Company Debug:", {
        extractedPolicyCompany,
        category,
        availableCompanies,
        currentPolicyCompany: formData.policyCompany
      });

      if (extractedPolicyCompany) {
        const matchedPolicyCompany = findClosestMatch(extractedPolicyCompany, availableCompanies);
        console.log("🎯 Policy Company Match Result:", {
          extracted: extractedPolicyCompany,
          matched: matchedPolicyCompany,
          availableOptions: availableCompanies
        });

        if (matchedPolicyCompany && matchedPolicyCompany !== formData.policyCompany) {
          console.log("✅ Setting Policy Company:", matchedPolicyCompany);
          setFormData(prev => ({ ...prev, policyCompany: matchedPolicyCompany }));
        }
      }
    }
  }, [category, availableCompanies, prefillData]);

  // Auto-fill policy type when category changes and policy types are available
  useEffect(() => {
    if (prefillData && category && availablePolicyTypes.length > 0) {
      const extractedPolicyType = prefillData.policy?.policyType || "";
      console.log("🔍 Auto-fill Policy Type Debug:", {
        extractedPolicyType,
        category,
        availablePolicyTypes,
        currentPolicyType: formData.policyType
      });

      if (extractedPolicyType) {
        const matchedPolicyType = findClosestMatch(extractedPolicyType, availablePolicyTypes);
        console.log("🎯 Policy Type Match Result:", {
          extracted: extractedPolicyType,
          matched: matchedPolicyType,
          availableOptions: availablePolicyTypes
        });

        if (matchedPolicyType && matchedPolicyType !== formData.policyType) {
          console.log("✅ Setting Policy Type:", matchedPolicyType);
          setFormData(prev => ({ ...prev, policyType: matchedPolicyType }));
        }
      }
    }
  }, [category, availablePolicyTypes, prefillData]);

  // Prefill form data when updating an existing policy
  useEffect(() => {
    console.log("🔄 Existing policy fetch useEffect triggered:", { mode, existingPolicyId, prefillData });
    const fetchExistingPolicy = async () => {
      // Don't fetch existing policy data if we have prefillData (PDF extracted data)
      if (prefillData) {
        console.log("🔄 Skipping existing policy fetch - we have prefillData");
        return;
      }

      console.log("🔄 Existing policy fetch context:", { mode, existingPolicyId, effectiveAgentId });

      if ((mode === "edit" || mode === "update") && existingPolicyId && effectiveAgentId) {
        console.log("🔄 Fetching existing policy for edit mode:", { mode, existingPolicyId, agentId: effectiveAgentId });
        try {
          const policyRef = doc(db, `agents/${effectiveAgentId}/policies/${existingPolicyId}`);
          const policySnap = await getDoc(policyRef);

          if (policySnap.exists()) {
            const policyData = policySnap.data();
            console.log("📄 Policy data fetched:", policyData);

            // Set category and subcategory first - map non-motor to health for display
            if (policyData.policyCategory === "non-motor") {
              setCategory("health");
              setSubCategory(policyData.policyCategoryType || "medical");
            } else {
              setCategory(policyData.policyCategory || "motor");
            }

            // Prefill all form data in one go to avoid multiple re-renders
            // Map vehicle type from database to display format
            const mapVehicleTypeFromDB = (vehicleType) => {
              switch (vehicleType) {
                case "four_wheeler":
                  return "Private Vehicle";
                case "two_wheeler":
                  return "Two Wheeler";
                case "commercial_vehicle":
                  return "Commercial Vehicle";
                default:
                  return vehicleType;
              }
            };

            // Store existing PDF URL for edit mode
            if (policyData.pdfUrl) {
              console.log("📄 Storing existing PDF URL for edit mode:", policyData.pdfUrl);
              setExistingPdfUrl(policyData.pdfUrl);
            }

            setCommissionData({
              commissionRate: policyData.commissionRate || 0,
              tdsRate: policyData.tdsRate || 2
            });

            const newFormData = {
              name: policyData.name || "",
              mobile: policyData.mobile || "",
              email: policyData.email || "",
              address: policyData.address || "",
              vehicleType: mapVehicleTypeFromDB(policyData.vehicleType) || "",
              vehicleNumber: policyData.vehicleNumber || "",
              vehicleMake: policyData.vehicleMake || "",
              vehicleModel: policyData.vehicleModel || "",
              policyCompany: policyData.policyCompany || "",
              policyType: policyData.policyType || "",
              policyNumber: policyData.policyNumber || "",
              sumInsured: policyData.sumInsured || "",
              expiryDate: policyData.expiryDate || "",
              premiumAmount: policyData.premiumAmount || "",
              paidAmount: policyData.paidAmount || "",
              modeOfPayment: policyData.modeOfPayment || "",
              remarks: policyData.remarks || "",
              referredBy: policyData.referredBy || "",
              netPremium: policyData.netPremium || "",
              discount: policyData.discount || "",
              tpPremium: policyData.tpPremium || "",
              odPremium: policyData.odPremium || "",
              amountPayable: policyData.amountPayable || "",
            };

            console.log("✅ Form data updated with policy data:", newFormData);
            setFormData(newFormData);
          } else {
            console.warn("❌ Policy not found in database");
          }
        } catch (error) {
          console.error("Error fetching existing policy:", error);
        }
      }
    };

    fetchExistingPolicy();
  }, [mode, existingPolicyId, effectiveAgentId]);

  // No longer needed - using bundled dropdown data

  // Update available makes when vehicle type changes
  useEffect(() => {
    if (formData.vehicleType) {
      const makes = getVehicleMakesByType(formData.vehicleType);
      setAvailableMakes(makes);
      // Clear vehicle make if it's not in the new list
      if (formData.vehicleMake && !makes.includes(formData.vehicleMake)) {
        setFormData(prev => ({ ...prev, vehicleMake: "" }));
      }
    } else {
      setAvailableMakes([]);
    }
  }, [formData.vehicleType, formData.vehicleMake]);

  // Update available companies when policy category changes
  useEffect(() => {
    if (category) {
      const companies = getCompaniesByCategory(category);
      setAvailableCompanies(companies);
      // Clear policy company if it's not in the new list
      if (formData.policyCompany && !companies.includes(formData.policyCompany)) {
        setFormData(prev => ({ ...prev, policyCompany: "" }));
      }
    } else {
      setAvailableCompanies([]);
    }
  }, [category, formData.policyCompany]);

  useEffect(() => {
    if (category) {
      const policyTypes = getPolicyTypesByCategory(category);
      setAvailablePolicyTypes(policyTypes);
      // Clear policy type if it's not in the new list
      if (formData.policyType && !policyTypes.includes(formData.policyType)) {
        setFormData(prev => ({ ...prev, policyType: "" }));
      }
    } else {
      setAvailablePolicyTypes([]);
    }
  }, [category, formData.policyType]);

  // Function to find closest match for auto-fill
  const findClosestMatch = (inputValue, options) => {
    if (!inputValue || !options || options.length === 0) return "";

    const normalizedInput = inputValue.toLowerCase().trim();
    console.log('🔍 Matching company:', { input: normalizedInput, options });

    // 1. Try exact match (case insensitive)
    const exactMatch = options.find(option =>
      option.toLowerCase().trim() === normalizedInput
    );
    if (exactMatch) {
      console.log('✅ Exact match:', exactMatch);
      return exactMatch;
    }

    // 2. Try removing common suffixes and match base name
    const commonSuffixes = [
      'general insurance', 'insurance', 'assurance', 'ltd', 'limited', 'company', 'co.', 'pvt', 'private', 'ltd.'
    ];

    // Clean the input by removing common suffixes and extra spaces
    let cleanedInput = normalizedInput;
    commonSuffixes.forEach(suffix => {
      cleanedInput = cleanedInput.replace(new RegExp(`\\s*${suffix}\\s*`, 'g'), ' ');
    });
    cleanedInput = cleanedInput.replace(/\s+/g, ' ').trim();

    // Try matching with cleaned input
    if (cleanedInput !== normalizedInput) {
      const cleanedMatch = options.find(option => {
        const optionLower = option.toLowerCase().trim();
        return optionLower === cleanedInput ||
          optionLower.startsWith(cleanedInput) ||
          optionLower.includes(cleanedInput);
      });

      if (cleanedMatch) {
        console.log('✅ Cleaned match:', cleanedMatch);
        return cleanedMatch;
      }
    }

    // 3. Try matching first two words (for cases like 'Go Digit General Insurance' -> 'Go Digit')
    const words = cleanedInput.split(' ');
    if (words.length > 2) {
      const firstTwoWords = words.slice(0, 2).join(' ');
      const firstTwoMatch = options.find(option =>
        option.toLowerCase().includes(firstTwoWords)
      );

      if (firstTwoMatch) {
        console.log('✅ First two words match:', firstTwoMatch);
        return firstTwoMatch;
      }
    }

    // 4. Try partial match (either way)
    const partialMatch = options.find(option => {
      const optionLower = option.toLowerCase();
      return (
        optionLower.includes(cleanedInput) ||
        cleanedInput.includes(optionLower) ||
        cleanedInput.split(' ').some(word => optionLower.includes(word)) ||
        optionLower.split(' ').some(word => cleanedInput.includes(word))
      );
    });

    if (partialMatch) {
      console.log('✅ Partial match:', partialMatch);
      return partialMatch;
    }

    // 5. Try first word match as last resort
    const firstWord = cleanedInput.split(' ')[0];
    if (firstWord && firstWord.length > 3) {
      const firstWordMatch = options.find(option =>
        option.toLowerCase().startsWith(firstWord)
      );

      if (firstWordMatch) {
        console.log('✅ First word match:', firstWordMatch);
        return firstWordMatch;
      }
    }

    console.log('❌ No suitable match found');
    return "";
  };

  // Initialize dropdowns on component mount
  // Initialize dropdowns on component mount
  useEffect(() => {
    if (isOpen) {
      if (formData.vehicleType) {
        setAvailableMakes(getVehicleMakesByType(formData.vehicleType));
      }
    }
  }, [isOpen, formData.vehicleType]);

  const validateReferredBy = (name) => {
    if (!name) return ""; // Optional field
    if (!/^[a-zA-Z\s]+$/.test(name)) return "Name should contain only alphabets";
    return "";
  };

  // Validation functions
  const validateName = (name) => {
    if (!name) return "Name is required";
    if (!/^[a-zA-Z\s]+$/.test(name)) return "Name should contain only alphabets";
    if (name.length < 2) return "Name should be at least 2 characters";
    if (name.length > 50) return "Name should not exceed 50 characters";
    return "";
  };

  const validateMobile = (mobile) => {
    if (!mobile) return "Mobile number is required";
    if (!/^\d+$/.test(mobile)) return "Mobile should contain only digits";
    if (mobile.length !== 10) return "Mobile should be exactly 10 digits";
    return "";
  };

  const validateEmail = (email) => {
    if (!email) return ""; // Email is optional
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return "Please enter a valid email address";
    return "";
  };

  const validateDate = (date) => {
    if (!date) return "Date is required";

    // Strict DD-MM-YYYY format validation
    const dateRegex = /^(0[1-9]|[12][0-9]|3[01])-(0[1-9]|1[0-2])-\d{4}$/;
    if (!dateRegex.test(date)) return "Date should be in DD-MM-YYYY format";

    // Parse using DD-MM-YYYY format to avoid MM-DD confusion
    const [day, month, year] = date.split('-');
    const inputDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
    const today = new Date();

    // Validate the parsed date is valid
    if (isNaN(inputDate.getTime())) return "Invalid date";

    if (inputDate < today) return "Date cannot be in the past";
    return "";
  };

  const validatePaidAmount = (amount) => {
    if (!amount) return "Amount received is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Please enter a valid amount";
    if (parseFloat(amount) < 0) return "Amount cannot be negative";

    // Check if paid amount exceeds premium amount
    const premiumAmount = parseFloat(formData.premiumAmount) || 0;
    if (parseFloat(amount) > premiumAmount) return `Amount cannot exceed premium amount (₹${premiumAmount})`;

    return "";
  };

  const validateNetPremium = (amount) => {
    if (!amount) return "Net Premium is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Amount should be a valid number";
    if (parseFloat(amount) <= 0) return "Amount should be greater than 0";
    return "";
  };

  const validateAmount = (amount) => {
    if (!amount) return "Amount is required";
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) return "Amount should be a valid number";
    if (parseFloat(amount) <= 0) return "Amount should be greater than 0";
    return "";
  };

  const validateVehicleNumber = (number) => {
    if (!number) return "Vehicle number is required";
    return "";
  };

  const validateMake = (make) => {
    if (!make) return "Make is required";
    return "";
  };

  const validateVehicleType = (type) => {
    if (!type) return "Vehicle type is required";
    return "";
  };

  const validateVehicleModel = (model) => {
    if (!model) return "Vehicle model is required";
    return "";
  };

  const validatePolicyNumber = (number) => {
    if (!number) return "Policy number is required";
    return "";
  };

  const validatePolicyType = (type) => {
    if (!type) return "Policy type is required";
    return "";
  };

  const validatePolicyCompany = (company) => {
    if (!company) return "Policy company is required";
    return "";
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate customer fields
    newErrors.name = validateName(formData.name);
    newErrors.mobile = validateMobile(formData.mobile);
    newErrors.mobile = validateMobile(formData.mobile);
    newErrors.email = validateEmail(formData.email);
    newErrors.referredBy = validateReferredBy(formData.referredBy);

    // Validate vehicle fields (only for motor category)
    if (category === "motor") {
      newErrors.vehicleType = validateVehicleType(formData.vehicleType);
      newErrors.vehicleNumber = validateVehicleNumber(formData.vehicleNumber);
      newErrors.vehicleMake = validateMake(formData.vehicleMake);
      newErrors.vehicleModel = validateVehicleModel(formData.vehicleModel);
    }

    // Validate policy fields
    newErrors.expiryDate = validateDate(formData.expiryDate);

    // Validate payment fields
    newErrors.netPremium = validateNetPremium(formData.netPremium);
    newErrors.premiumAmount = validateAmount(formData.premiumAmount);
    newErrors.paidAmount = validatePaidAmount(formData.paidAmount);

    // Validate policy fields
    newErrors.policyNumber = validatePolicyNumber(formData.policyNumber);
    newErrors.policyType = validatePolicyType(formData.policyType);
    newErrors.policyCompany = validatePolicyCompany(formData.policyCompany);

    setErrors(newErrors);

    // Check if there are any errors
    return !Object.values(newErrors).some(error => error !== "");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let processedValue = value;

    // Apply input restrictions based on field type
    switch (name) {
      case 'mobile':
        // Only allow digits for mobile
        processedValue = value.replace(/[^0-9]/g, '');
        break;
      case 'premiumAmount':
      case 'paidAmount':
      case 'sumInsured':
      case 'netPremium':
      case 'tpPremium':
      case 'odPremium':
      case 'discount':
      case 'amountPayable':
        // Only allow digits and decimal point for amounts
        processedValue = value.replace(/[^0-9.]/g, '');
        // Prevent multiple decimal points
        const decimalCount = (processedValue.match(/\./g) || []).length;
        if (decimalCount > 1) {
          const parts = processedValue.split('.');
          processedValue = parts[0] + '.' + parts.slice(1).join('');
        }
        // Limit to 2 decimal places
        if (processedValue.includes('.')) {
          const parts = processedValue.split('.');
          if (parts[1] && parts[1].length > 2) {
            processedValue = parts[0] + '.' + parts[1].substring(0, 2);
          }
        }
        break;
      case 'name':
      case 'policyType':
      case 'vehicleModel':
        // Only allow letters and spaces, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        break;
      case 'vehicleNumber':
        // Allow letters, numbers, and hyphens, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9-]/g, '').toUpperCase();
        break;
      case 'policyNumber':
        // Allow alphanumeric and common symbols, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9\-_\/]/g, '').toUpperCase();
        break;
      case 'expiryDate':
        // Only allow digits, hyphens, and forward slashes for date format
        processedValue = value.replace(/[^0-9-\/]/g, '');
        break;
      case 'email':
        // Allow email characters, keep lowercase
        processedValue = value.toLowerCase();
        break;
      case 'address':
        // Allow letters, numbers, spaces, and common punctuation, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z0-9\s,.-]/g, '').toUpperCase();
        break;
      case 'remarks':
        // Allow alphanumeric and special characters, keep original case
        processedValue = value; // No transformation needed for remarks
        break;
      case 'referredBy':
        // Same as name: Only allow letters and spaces, auto-capitalize
        processedValue = value.replace(/[^a-zA-Z\s]/g, '').toUpperCase();
        break;
      default:
        // For other fields, auto-capitalize alphanumeric content
        if (value.match(/[a-zA-Z]/)) {
          processedValue = value.toUpperCase();
        }
    }

    setFormData(prev => {
      const newData = { ...prev, [name]: processedValue };

      // Auto-calculate Amount Payable: Total Premium - Customer Discount
      if (name === 'premiumAmount' || name === 'discount') {
        const total = parseFloat(newData.premiumAmount) || 0;
        const disc = parseFloat(newData.discount) || 0;
        newData.amountPayable = (total - disc).toString();
      }

      return newData;
    });

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async () => {
    if (isSubmitting) return; // Prevent multiple submissions

    // Validate form before submission
    if (!validateForm()) {
      setShowValidationBanner(true);
      // Hide banner after 3 seconds
      setTimeout(() => setShowValidationBanner(false), 3000);
      return;
    }

    try {
      setIsSubmitting(true);

      let pdfUrl = "";

      // For edit mode, preserve existing PDF URL
      if (mode === "edit" && existingPdfUrl) {
        pdfUrl = existingPdfUrl;
        console.log("✅ Edit mode: Preserving existing PDF URL:", pdfUrl);
      } else {
        // For renewal/update mode, use new PDF URL
        pdfUrl = finalPdfUrl || (prefillData?.pdfUrl ?? "");
        const fileToUpload = pdfFileState || pdfFile; // Use local state or prop
        if (!pdfUrl && fileToUpload) {
          try {
            const storageRef = ref(storage, `policies/${effectiveAgentId}/${Date.now()}_${fileToUpload.name}`);
            const snapshot = await uploadBytes(storageRef, fileToUpload);
            pdfUrl = await getDownloadURL(snapshot.ref);
            console.log("✅ PDF uploaded during submission, URL:", pdfUrl);
            setFinalPdfUrl(pdfUrl);
          } catch (uploadError) {
            console.error("❌ PDF upload failed during submission:", uploadError);
            toast.error("Failed to upload policy PDF.");
            return;
          }
        }
        // Only show error if user is in create mode and using PDF upload flow (pdfFile is present)
        if (!pdfUrl && pdfFile) {
          toast.error("PDF not uploaded yet. Please upload and extract first.");
          return;
        }
        console.log("✅ Renewal/Update mode: Using new PDF URL:", pdfUrl);
      }

      console.log("🔄 Policy update mode:", { mode, existingPolicyId, existingCustomerId });

      // For edit mode, update the same policy. For renewal/update, create a NEW policy
      const policyId = mode === "edit" ? existingPolicyId : uuidv4();
      const oldPolicyId = (mode === "update" || mode === "renew") ? existingPolicyId : null;

      console.log("🔄 Using policyId:", policyId, "customerId:", existingCustomerId, "oldPolicyId:", oldPolicyId);
      const customerId = existingCustomerId || uuidv4();
      const customerPath = `agents/${effectiveAgentId}/customers/${customerId}`;

      const customerPayload = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        address: formData.address,
        referredBy: formData.referredBy || "",
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        agentId: effectiveAgentId,
      };

      // If we are updating or reusing an existing customer, merge to avoid clobbering fields
      if (mode === "edit" || mode === "update" || mode === "renew" || existingCustomerId) {
        await setDoc(doc(db, customerPath), customerPayload, { merge: true });
      } else {
        await setDoc(doc(db, customerPath), customerPayload);
      }

      const docPath = `agents/${effectiveAgentId}/policies/${policyId}`;

      let policyCategoryType = "vehicle";
      let finalPolicyCategory = category;

      if (category === "health") {
        // Map health to non-motor for storage
        finalPolicyCategory = "non-motor";
        policyCategoryType = subCategory;
      } else if (category === "non-motor") {
        policyCategoryType = subCategory;
      } else if (category === "life") {
        policyCategoryType = "life";
      }

      // Map vehicle type for database storage
      const mapVehicleTypeForDB = (vehicleType) => {
        switch (vehicleType) {
          case "Private Vehicle":
            return "four_wheeler";
          case "Two Wheeler":
            return "two_wheeler";
          case "Commercial Vehicle":
            return "commercial_vehicle";
          default:
            return vehicleType;
        }
      };

      // Calculate Commission
      const commRate = commissionData.commissionRate;
      const tds = commissionData.tdsRate;
      const net = parseFloat(formData.netPremium) || 0;
      const payable = parseFloat(formData.amountPayable) || parseFloat(formData.premiumAmount) || net; // Fallback logic
      const rcvd = parseFloat(formData.paidAmount) || 0;

      const baseComm = (net * (commRate / 100));
      const discEffect = (net - payable);
      const grossCommTotal = commRate > 0 ? (baseComm - discEffect) : 0;
      const currentGross = commRate > 0 ? (grossCommTotal - (payable - rcvd)) : 0;
      const totalComm = Math.max(0, currentGross * (1 - (tds / 100)));

      const policyPayload = {
        ...formData,
        totalAmount: formData.premiumAmount,
        receivedAmount: formData.paidAmount,
        vehicleType: mapVehicleTypeForDB(formData.vehicleType),
        amountPayable: formData.amountPayable || formData.premiumAmount,
        agentId: effectiveAgentId,
        policyCategory: finalPolicyCategory,
        policyCategoryType,
        customerId,
        pdfUrl,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        commissionRate: commRate,
        tdsRate: tds,
        grossCommission: currentGross,
        totalCommission: totalComm
      };

      // Save the new/updated policy
      if (mode === "edit") {
        // Edit mode: update existing policy, preserve creation date
        delete policyPayload.createdAt;
        await setDoc(doc(db, docPath), policyPayload, { merge: true });
        console.log("✅ Policy updated (edit mode):", policyId);
      } else {
        // Create mode or Renewal mode: create new policy
        await setDoc(doc(db, docPath), policyPayload);
        console.log("✅ New policy created:", policyId);
      }

      // Invalidate cache immediately after write to ensure UI shows fresh data
      firebaseCache.invalidateAfterWrite('renewals_write', effectiveAgentId);
      firebaseCache.invalidateAfterWrite('policydetails_write', effectiveAgentId);

      // For renewal/update mode, delete the old policy and its PDF
      if (oldPolicyId && (mode === "update" || mode === "renew")) {
        try {
          console.log("🗑️ Deleting old policy:", oldPolicyId);

          // First, get the old policy to find its PDF URL
          const oldPolicyRef = doc(db, `agents/${effectiveAgentId}/policies/${oldPolicyId}`);
          const oldPolicySnap = await getDoc(oldPolicyRef);

          if (oldPolicySnap.exists()) {
            const oldPolicyData = oldPolicySnap.data();

            // Delete old PDF from storage if it exists
            if (oldPolicyData.pdfUrl) {
              try {
                const { deleteObject } = await import("firebase/storage");
                // Extract the file path from the URL
                const url = new URL(oldPolicyData.pdfUrl);
                const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
                if (pathMatch) {
                  const filePath = decodeURIComponent(pathMatch[1]);
                  const oldFileRef = ref(storage, filePath);
                  await deleteObject(oldFileRef);
                  console.log("✅ Old PDF deleted from storage");
                }
              } catch (pdfError) {
                console.warn("⚠️ Could not delete old PDF file:", pdfError);
                // Continue with policy deletion even if PDF deletion fails
              }
            }

            // Delete the old policy document
            const { deleteDoc } = await import("firebase/firestore");
            await deleteDoc(oldPolicyRef);
            console.log("✅ Old policy document deleted:", oldPolicyId);
          }
        } catch (deleteError) {
          console.error("❌ Error deleting old policy:", deleteError);
          // Don't fail the entire operation if deletion fails
          toast.warning("New policy created but could not delete old policy");
        }
      }

      setFinalPdfUrl(pdfUrl);
      // Prefer parent-controlled success dialog if provided
      if (onSuccess) {
        // Pass both pdfUrl and the complete updated policy data
        onSuccess(pdfUrl, { id: policyId, ...policyPayload });
      } else {
        setShowSuccess(true);
      }
      onClose();
      setFormData({
        name: "",
        mobile: "",
        email: "",
        address: "",
        vehicleType: "",
        vehicleNumber: "",
        vehicleMake: "",
        vehicleModel: "",
        policyCompany: "",
        policyType: "",
        policyNumber: "",
        sumInsured: "",
        expiryDate: "",
        premiumAmount: "",
        paidAmount: "",
        modeOfPayment: "",
        remarks: ""
      });
      setFile(null);
    } catch (err) {
      console.error("Failed to upload policy:", err);
      toast.error("Error saving policy.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Only hide if both form and success are not shown
  if (!isOpen && !showSuccess) {
    return null;
  }

  return (
    <>
      {isOpen && (
        <div className="unified-modal-overlay">
          <div
            className="unified-modal-content"
            style={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '1037px',
              width: '100%',
              height: '840px',
              borderRadius: '12px',
              background: '#FFFFFF',
              padding: '20px 32px',
              gap: '32px',
              position: 'relative'
            }}
          >
            {/* Close button moved into header below */}
            {/* Header */}
            <div
              style={{
                marginTop: '16px',
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                maxWidth: '973px',
                width: '100%',
                height: '28px',
                flexShrink: 0,
              }}
            >
              <h2
                style={{
                  margin: 0,
                  fontFamily: "Roboto Slab",
                  fontWeight: 500,
                  fontSize: "32px",
                  lineHeight: "56px",
                  letterSpacing: "0.02em",
                  color: "#212121",
                }}
              >
                {mode === "edit" ? "Edit Policy" : "Renew Policy"}
              </h2>
              <IconButton
                onClick={onClose}
                size="small"
                sx={{
                  "&:hover": {
                    backgroundColor: "rgba(0,0,0,0.04)",
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </div>

            {/* Category Radios */}
            <div
              style={{
                marginTop: '8px',
                marginBottom: '16px',
                display: 'flex',
                gap: '32px',
                alignItems: 'center',
                margin: '0 auto',
                maxWidth: '973px',
                width: '100%',
                height: '28px',
                flexShrink: 0,
              }}
            >
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="motor"
                  checked={category === "motor"}
                  onChange={() => setCategory("motor")}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "motor" ? '#0063F7' : '#000000',
                  }}
                >
                  Motor Insurance
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="life"
                  checked={category === "life"}
                  onChange={() => setCategory("life")}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "life" ? '#0063F7' : '#000000',
                  }}
                >
                  Life Insurance
                </span>
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                <input
                  type="radio"
                  name="policyCategory"
                  value="health"
                  checked={category === "health"}
                  onChange={() => setCategory("health")}
                  style={{ accentColor: '#007bff', width: '18px', height: '18px' }}
                />
                <span
                  style={{
                    fontFamily: 'Roboto',
                    fontWeight: 400,
                    fontSize: '16px',
                    lineHeight: '20px',
                    letterSpacing: '1%',
                    color: category === "health" ? '#0063F7' : '#000000',
                  }}
                >
                  Non Motor Insurance
                </span>
              </label>
            </div>

            {/* Scrollable Content */}
            <div
              style={{
                maxWidth: '973px',
                width: '100%',
                height: '548px',
                padding: '8px',
                gap: '10px',
                overflowY: 'auto',
                overflowX: 'hidden',
                flexGrow: 1,
              }}
            >
              <form>
                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: '16px'
                  }}
                >
                  Customer Details
                </h3>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  <TextField
                    variant="outlined"
                    label="Mobile"
                    name="mobile"
                    value={formData.mobile}
                    onChange={handleChange}
                    error={!!errors.mobile}
                    helperText={errors.mobile}
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric",
                      pattern: "[0-9]*"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={!!errors.name}
                    helperText={errors.name}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  <TextField
                    variant="outlined"
                    label="E-mail"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    error={!!errors.email}
                    helperText={errors.email}
                    inputProps={{
                      maxLength: 100,
                      inputMode: "email",
                      autoCapitalize: "none"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Referred by"
                    name="referredBy"
                    value={formData.referredBy || ""}
                    onChange={handleChange}
                    error={!!errors.referredBy}
                    helperText={errors.referredBy}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  <TextField
                    variant="outlined"
                    label="Address"
                    name="address"
                    value={formData.address}
                    onChange={handleChange}
                    inputProps={{
                      maxLength: 200,
                      autoCapitalize: "words"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>

                {category === "motor" && (
                  <>
                    <h3
                      style={{
                        fontFamily: 'Roboto Slab',
                        fontWeight: 500,
                        fontSize: '20px',
                        lineHeight: '32px',
                        letterSpacing: '1%',
                        color: '#212121',
                        marginBottom: '16px'
                      }}
                    >
                      Vehicle Details
                    </h3>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                      <Autocomplete
                        options={vehicleTypes}
                        value={formData.vehicleType}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({ ...prev, vehicleType: newValue || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Type"
                            variant="outlined"
                            sx={autocompleteStyles}
                            error={!!errors.vehicleType}
                            helperText={errors.vehicleType}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    color: '#757575',
                                    position: 'absolute',
                                    right: '12px',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ),
                            }}
                          />
                        )}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disableClearable
                      />
                      <TextField
                        variant="outlined"
                        label="Registration No"
                        name="vehicleNumber"
                        value={formData.vehicleNumber}
                        onChange={handleChange}
                        error={!!errors.vehicleNumber}
                        helperText={errors.vehicleNumber}
                        inputProps={{
                          maxLength: 20,
                          autoCapitalize: "characters"
                        }}
                        sx={textFieldStyles}
                      />
                    </Box>
                    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                      <Autocomplete
                        options={availableMakes}
                        value={formData.vehicleMake}
                        onChange={(event, newValue) => {
                          setFormData(prev => ({ ...prev, vehicleMake: newValue || "" }));
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Make"
                            variant="outlined"
                            sx={autocompleteStyles}
                            disabled={!formData.vehicleType}
                            error={!!errors.vehicleMake}
                            helperText={errors.vehicleMake}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <KeyboardArrowDownIcon
                                  sx={{
                                    color: '#757575',
                                    position: 'absolute',
                                    right: '12px',
                                    pointerEvents: 'none'
                                  }}
                                />
                              ),
                            }}
                          />
                        )}
                        freeSolo
                        selectOnFocus
                        clearOnBlur={false}
                        handleHomeEndKeys
                        disabled={!formData.vehicleType}
                        disableClearable
                      />
                      <TextField
                        variant="outlined"
                        label="Model"
                        name="vehicleModel"
                        value={formData.vehicleModel}
                        onChange={handleChange}
                        error={!!errors.vehicleModel}
                        helperText={errors.vehicleModel}
                        inputProps={{
                          maxLength: 50,
                          autoCapitalize: "words"
                        }}
                        sx={textFieldStyles}
                      />
                    </Box>
                  </>
                )}

                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: '16px'
                  }}
                >
                  Policy Details
                </h3>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  <Autocomplete
                    options={availableCompanies}
                    value={formData.policyCompany}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, policyCompany: newValue || "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Company"
                        variant="outlined"
                        sx={autocompleteStyles}
                        error={!!errors.policyCompany}
                        helperText={errors.policyCompany}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <KeyboardArrowDownIcon
                              sx={{
                                color: '#757575',
                                position: 'absolute',
                                right: '12px',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                    disableClearable
                  />
                  <TextField
                    variant="outlined"
                    label="Policy Number"
                    name="policyNumber"
                    value={formData.policyNumber}
                    onChange={handleChange}
                    error={!!errors.policyNumber}
                    helperText={errors.policyNumber}
                    inputProps={{
                      maxLength: 50,
                      autoCapitalize: "characters"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  <Autocomplete
                    options={availablePolicyTypes}
                    value={formData.policyType}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, policyType: newValue || "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Policy Type"
                        variant="outlined"
                        sx={autocompleteStyles}
                        error={!!errors.policyType}
                        helperText={errors.policyType}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <KeyboardArrowDownIcon
                              sx={{
                                color: '#757575',
                                position: 'absolute',
                                right: '12px',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                    disableClearable
                  />
                  <TextField
                    variant="outlined"
                    label="Expiry Date"
                    name="expiryDate"
                    value={formData.expiryDate}
                    onChange={handleChange}
                    error={!!errors.expiryDate}
                    helperText={errors.expiryDate}
                    placeholder="DD-MM-YYYY"
                    inputProps={{
                      maxLength: 10,
                      inputMode: "numeric"
                    }}
                    sx={textFieldStyles}
                  />
                </Box>
                {category !== "motor" && (
                  <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                    <TextField
                      variant="outlined"
                      label="Sum Insured"
                      name="sumInsured"
                      value={formData.sumInsured}
                      onChange={handleChange}
                      inputProps={{
                        maxLength: 15,
                        inputMode: "decimal"
                      }}
                      sx={textFieldStyles}
                    />
                    <Box />
                  </Box>
                )}

                <h3
                  style={{
                    fontFamily: 'Roboto Slab',
                    fontWeight: 500,
                    fontSize: '20px',
                    lineHeight: '32px',
                    letterSpacing: '1%',
                    color: '#212121',
                    marginBottom: '16px'
                  }}
                >
                  Payment
                </h3>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: '20px', mb: '24px' }}>
                  {category === "motor" && (
                    <>
                      {formData.policyType !== "Liability" && (
                        <TextField
                          variant="outlined"
                          label="OD Premium"
                          name="odPremium"
                          value={formData.odPremium}
                          onChange={handleChange}
                          inputProps={{
                            inputMode: "decimal"
                          }}
                          sx={textFieldStyles}
                        />
                      )}
                      {(formData.policyType === "Package" || formData.policyType === "Liability") && (
                        <TextField
                          variant="outlined"
                          label="TP Premium"
                          name="tpPremium"
                          value={formData.tpPremium}
                          onChange={handleChange}
                          inputProps={{
                            inputMode: "decimal"
                          }}
                          sx={textFieldStyles}
                        />
                      )}
                    </>
                  )}
                  <TextField
                    variant="outlined"
                    label="Net Premium"
                    name="netPremium"
                    value={formData.netPremium}
                    onChange={handleChange}
                    error={!!errors.netPremium}
                    helperText={errors.netPremium}
                    inputProps={{
                      maxLength: 15,
                      inputMode: "decimal"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Total Premium"
                    name="premiumAmount"
                    value={formData.premiumAmount}
                    onChange={handleChange}
                    error={!!errors.premiumAmount}
                    helperText={errors.premiumAmount}
                    inputProps={{
                      maxLength: 15,
                      inputMode: "decimal"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Customer Discount"
                    name="discount"
                    value={formData.discount}
                    onChange={handleChange}
                    error={!!errors.discount}
                    helperText={errors.discount}
                    inputProps={{
                      maxLength: 15,
                      inputMode: "decimal"
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Amount Payable"
                    name="amountPayable"
                    value={formData.amountPayable}
                    onChange={handleChange}
                    error={!!errors.amountPayable}
                    helperText={errors.amountPayable}
                    inputProps={{
                      maxLength: 15,
                      inputMode: "decimal",
                      readOnly: true
                    }}
                    sx={textFieldStyles}
                  />
                  <TextField
                    variant="outlined"
                    label="Amount Received"
                    name="paidAmount"
                    value={formData.paidAmount}
                    onChange={handleChange}
                    error={!!errors.paidAmount}
                    helperText={errors.paidAmount}
                    inputProps={{
                      maxLength: 15,
                      inputMode: "decimal"
                    }}
                    sx={textFieldStyles}
                  />
                  <Autocomplete
                    options={paymentModes}
                    value={formData.modeOfPayment}
                    onChange={(event, newValue) => {
                      setFormData(prev => ({ ...prev, modeOfPayment: newValue || "" }));
                    }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Mode of Payment"
                        variant="outlined"
                        sx={autocompleteStyles}
                        InputProps={{
                          ...params.InputProps,
                          endAdornment: (
                            <KeyboardArrowDownIcon
                              sx={{
                                color: '#757575',
                                position: 'absolute',
                                right: '12px',
                                pointerEvents: 'none'
                              }}
                            />
                          ),
                        }}
                      />
                    )}
                    freeSolo
                    selectOnFocus
                    clearOnBlur={false}
                    handleHomeEndKeys
                    disableClearable
                  />

                  <TextField
                    variant="outlined"
                    label="Remarks"
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleChange}
                    multiline
                    rows={2}
                    inputProps={{
                      maxLength: 200,
                      autoCapitalize: "sentences"
                    }}
                    sx={{
                      ...textFieldStyles,
                      "& .MuiInputBase-root": {
                        ...textFieldStyles["& .MuiInputBase-root"],
                        height: "auto",
                        minHeight: "56px"
                      }
                    }}
                  />
                </Box>
              </form>
            </div>

            {/* Footer */}
            <Box
              sx={{
                padding: "16px 24px",
                display: "flex",
                justifyContent: "flex-end",
                gap: "12px",
                flexShrink: 0,
              }}
            >
              {/* Cancel Button */}
              <Button
                onClick={onClose}
                disabled={isSubmitting}
                variant="outlined"
                sx={{
                  width: "149px",
                  height: "60px",
                  borderRadius: "8px",
                  borderColor: "#B00020",
                  color: "#B00020",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "20px",
                  lineHeight: "24px",
                  letterSpacing: "0.01em",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "inherit",
                    color: "#B00020",
                    boxShadow: 10
                  }
                }}
              >
                Cancel
              </Button>

              {/* Submit Button */}
              <Button
                variant="contained"
                onClick={handleSubmit}
                disabled={isSubmitting}
                sx={{
                  width: "149px",
                  height: "60px",
                  px: "12px",
                  py: "16px",
                  borderRadius: "8px",
                  backgroundColor: isSubmitting ? "#7e9ed8" : "#0063F7 !important",
                  color: "#FFFFFF",
                  fontFamily: "Roboto",
                  fontWeight: 400,
                  fontSize: "18px",
                  lineHeight: "22px",
                  letterSpacing: "0.5px",
                  textTransform: "none",
                  whiteSpace: "nowrap",
                  position: "relative",
                  transition: "all 0.3s ease",
                  "&:hover:not(:disabled)": {
                    backgroundColor: "#0051d4 !important",
                    transform: "translateY(-1px)",
                    boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)"
                  },
                  "&:active:not(:disabled)": {
                    transform: "translateY(0)"
                  },
                  "&:disabled": {
                    cursor: "not-allowed",
                    backgroundColor: "#7e9ed8 !important"
                  }
                }}
              >
                {isSubmitting ? (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={20} thickness={4} sx={{ color: '#fff' }} />
                    {mode === "edit" ? "Updating..." : "Renewing..."}
                  </Box>
                ) : (
                  mode === "edit" ? "Update Policy" : (mode === "renew" ? "Renew Policy" : "Renew Policy")
                )}
              </Button>
            </Box>
          </div>
        </div >
      )}

      {
        showValidationBanner && (
          <Box
            sx={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: 10000,
              backgroundColor: '#FEE2E2',
              color: '#FFFFFF',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: 500,
              padding: '12px 16px',
              minHeight: '44px',
              alignItems: 'center',
              gap: '2px',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              display: 'inline-flex',
              maxWidth: '90vw',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            <svg
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{ color: '#FFFFFF', marginRight: '8px' }}
            >
              <path
                d="M12 9V13M12 17H12.01M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            Please fill all mandatory fields
          </Box>
        )
      }

      {
        showSuccess && (
          <SuccessDialog
            isOpen={true}
            pdfUrl={finalPdfUrl}
            headerText="Renewal Success"
            messageText="Policy renewal completed. Share with customer via WhatsApp."
            agentName={user?.displayName || "Your Agent"}
            premiumAmount={formData.amountPayable}
            balanceAmount={Math.max((Number(formData.amountPayable) || 0) - (Number(formData.paidAmount) || 0), 0)}
            onClose={() => {
              setShowSuccess(false);
            }}
          />
        )
      }
    </>
  );
};

export default RenewPolicy;
